import {
  can,
  CAPABILITY,
  getActorRole,
  isAdmin,
  isEmployee,
  isManager,
  resolveOrgPolicies,
  ROLE,
} from "./permissions";
import { isGuestActor } from "./guestMode";
import { isFinanceMode } from "./nexoraOpsAccess";
import { canOpenFinanceDirectory } from "../navigation/financeWorkspace";
import {
  actorCanAccessPlayStoreReadiness,
  PLAY_STORE_READINESS_SCREEN,
} from "./playStoreReadinessAccess";
import {
  actorCanAccessGoogleVisibility,
  GOOGLE_VISIBILITY_SCREEN,
} from "./googleVisibilityAccess";
import {
  actorCanAccessFeedbackInbox,
  FEEDBACK_INBOX_SCREEN,
} from "../feedback/feedbackInboxAccess";

/**
 * Maps App.js SCREEN ids to permission capabilities for route guards.
 */
const AUTH_FLOW_SCREENS = new Set([
  "onboarding",
  "email_confirmation",
  "update_password",
  "org_setup",
  "payroll_print_isolation",
  "start",
  "question",
  "review",
  "complete",
  "request",
  // Personal user settings — any authenticated user; not org capability-gated.
  "user_preferences",
  // Unified Settings hub — any authenticated user; content within is
  // filtered per-viewer by capability (see settingsSectionAccess.js).
  "settings",
  // Send Feedback — any signed-in user. The database still requires an
  // active organization membership before a row can be stored.
  "feedback",
  // Ashley Home discovery — reachable from the CrewPilot logo for any signed-in user.
  "ashley_home",
]);

export const SCREEN_CAPABILITY_MAP = Object.freeze({
  mode_select: CAPABILITY.SCREEN_MODE_SELECT,
  kpi_dashboard: CAPABILITY.SCREEN_KPI_DASHBOARD,
  time_tracking_v2: CAPABILITY.SCREEN_TIME_TRACKING,
  crewpilot: CAPABILITY.SCREEN_COCKPIT,
  manual_time_entry: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
  create_client: CAPABILITY.SCREEN_CREATE_CLIENT,
  create_project: CAPABILITY.SCREEN_CREATE_PROJECT,
  project_detail: CAPABILITY.SCREEN_PROJECT_DETAIL,
  intelligence: CAPABILITY.SCREEN_INTELLIGENCE,
  report_builder: CAPABILITY.REPORT_BUILDER_VIEW,
  project_analytics: CAPABILITY.PROJECT_ANALYTICS_VIEW,
  payroll_home: CAPABILITY.SCREEN_PAYROLL_HOME,
  payroll_queue: CAPABILITY.PAYROLL_QUEUE,
  payroll_report: CAPABILITY.PAYROLL_REPORT,
  user_management: CAPABILITY.SCREEN_USER_MANAGEMENT,
  administration: CAPABILITY.SCREEN_ADMINISTRATION,
  estimate_hub: CAPABILITY.SCREEN_ESTIMATE_HUB,
  estimate_pipeline: CAPABILITY.ESTIMATE_VIEW,
  invoice_hub: CAPABILITY.SCREEN_INVOICE_HUB,
  materials: CAPABILITY.SCREEN_MATERIALS,
  tasks: CAPABILITY.SCREEN_TASKS,
  planning: CAPABILITY.SCREEN_PLANNING,
  accounts_payable: CAPABILITY.SCREEN_ACCOUNTS_PAYABLE,
  accounts_receivable: CAPABILITY.SCREEN_ACCOUNTS_RECEIVABLE,
  mission_control: CAPABILITY.SCREEN_MISSION_CONTROL,
  open_session: CAPABILITY.SCREEN_TIME_TRACKING,
  user_preferences: CAPABILITY.SCREEN_USER_PREFERENCES,
  settings: CAPABILITY.SCREEN_SETTINGS,
  finance_dashboard: CAPABILITY.SCREEN_FINANCE,
  finance_accounts: CAPABILITY.SCREEN_FINANCE,
  finance_transactions: CAPABILITY.SCREEN_FINANCE,
  finance_money_in: CAPABILITY.SCREEN_FINANCE,
  finance_money_out: CAPABILITY.SCREEN_FINANCE,
  finance_transfer: CAPABILITY.SCREEN_FINANCE,
  financial_position: CAPABILITY.SCREEN_FINANCE,
  quick_create: CAPABILITY.SCREEN_KPI_DASHBOARD,
});

export const MODE_CAPABILITY_MAP = Object.freeze({
  crewpilot: CAPABILITY.SCREEN_COCKPIT,
  intelligence: CAPABILITY.SCREEN_INTELLIGENCE,
  user_management: CAPABILITY.SCREEN_USER_MANAGEMENT,
  administration: CAPABILITY.SCREEN_ADMINISTRATION,
  payroll: CAPABILITY.SCREEN_PAYROLL_HOME,
  accounts_payable: CAPABILITY.SCREEN_ACCOUNTS_PAYABLE,
  accounts_receivable: CAPABILITY.SCREEN_ACCOUNTS_RECEIVABLE,
  materials: CAPABILITY.SCREEN_MATERIALS,
  tasks: CAPABILITY.SCREEN_TASKS,
  planning: CAPABILITY.SCREEN_PLANNING,
  estimates: CAPABILITY.SCREEN_ESTIMATE_HUB,
  invoices: CAPABILITY.SCREEN_INVOICE_HUB,
  mission: CAPABILITY.SCREEN_MISSION_CONTROL,
  finance: CAPABILITY.SCREEN_FINANCE,
  manual_time_entry: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
  clients: CAPABILITY.SCREEN_CREATE_CLIENT,
});

export const DASHBOARD_SCREEN_CAPABILITY_MAP = Object.freeze({
  kpi_dashboard: CAPABILITY.SCREEN_KPI_DASHBOARD,
  accounts_payable: CAPABILITY.ACCOUNTS_PAYABLE_VIEW,
  accounts_receivable: CAPABILITY.ACCOUNTS_RECEIVABLE_VIEW,
  financial_position: CAPABILITY.SCREEN_FINANCE,
  estimate_pipeline: CAPABILITY.ESTIMATE_VIEW,
  create_client: CAPABILITY.SCREEN_CREATE_CLIENT,
  create_project: CAPABILITY.SCREEN_CREATE_PROJECT,
  manual_time_entry: CAPABILITY.SCREEN_MANUAL_TIME_ENTRY,
});

const DEFAULT_DENIED_SCREEN = "mode_select";
const DEFAULT_ALLOWED_SCREEN = "kpi_dashboard";

export function getCapabilityForScreen(screenId) {
  return SCREEN_CAPABILITY_MAP[screenId] || null;
}

export function canAccessScreen(
  actor,
  screenId,
  context = {},
  orgPolicies,
  directReportIds,
) {
  if (!screenId || AUTH_FLOW_SCREENS.has(screenId)) {
    return true;
  }

  if (screenId === PLAY_STORE_READINESS_SCREEN) {
    return actorCanAccessPlayStoreReadiness(actor);
  }

  if (screenId === GOOGLE_VISIBILITY_SCREEN) {
    return actorCanAccessGoogleVisibility(actor);
  }

  if (screenId === FEEDBACK_INBOX_SCREEN) {
    return actorCanAccessFeedbackInbox(actor);
  }

  // Directory shell. Cash screens below stay on SCREEN_FINANCE.
  if (screenId === "finance_dashboard") {
    return canOpenFinanceDirectory(actor, orgPolicies);
  }

  const capability = getCapabilityForScreen(screenId);

  if (!capability) {
    return false;
  }

  return can(
    actor,
    capability,
    {
      ...context,
      directReportIds: context.directReportIds ?? directReportIds ?? [],
    },
    orgPolicies,
  );
}

export function canAccessMode(actor, mode, orgPolicies) {
  if (mode === "feedback") {
    return Boolean(actor) && !isGuestActor(actor);
  }

  if (mode === PLAY_STORE_READINESS_SCREEN) {
    return actorCanAccessPlayStoreReadiness(actor);
  }

  if (mode === GOOGLE_VISIBILITY_SCREEN) {
    return actorCanAccessGoogleVisibility(actor);
  }

  if (mode === FEEDBACK_INBOX_SCREEN) {
    return actorCanAccessFeedbackInbox(actor);
  }

  if (isFinanceMode(mode)) {
    return canOpenFinanceDirectory(actor, orgPolicies);
  }

  const capability = MODE_CAPABILITY_MAP[mode];

  if (!capability) {
    return false;
  }

  return can(actor, capability, {}, orgPolicies);
}

export function canAccessDashboardScreen(actor, screenId, orgPolicies) {
  const capability = DASHBOARD_SCREEN_CAPABILITY_MAP[screenId];

  if (!capability) {
    return false;
  }

  return can(actor, capability, {}, orgPolicies);
}

export function getDeniedRedirectScreen(actor, orgPolicies) {
  if (can(actor, CAPABILITY.SCREEN_MODE_SELECT, {}, orgPolicies)) {
    return DEFAULT_DENIED_SCREEN;
  }

  return DEFAULT_ALLOWED_SCREEN;
}

export function resolveSafeBootScreen(actor, lastScreen, orgPolicies) {
  const policies = orgPolicies || resolveOrgPolicies();

  if (!actor) {
    return "onboarding";
  }

  if (isGuestActor(actor)) {
    if (
      lastScreen &&
      !AUTH_FLOW_SCREENS.has(lastScreen) &&
      canAccessScreen(actor, lastScreen, {}, policies)
    ) {
      return lastScreen;
    }
    return DEFAULT_ALLOWED_SCREEN;
  }

  if (
    lastScreen &&
    !AUTH_FLOW_SCREENS.has(lastScreen) &&
    canAccessScreen(actor, lastScreen, {}, policies)
  ) {
    return lastScreen;
  }

  if (can(actor, CAPABILITY.SCREEN_KPI_DASHBOARD, {}, policies)) {
    return DEFAULT_ALLOWED_SCREEN;
  }

  return DEFAULT_DENIED_SCREEN;
}

export { isGuestModeAllowed } from "./guestMode";

export { ROLE, getActorRole, isAdmin, isManager, isEmployee };
